import { useState } from 'react';
import { Formik, Field, FieldProps, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  useTranslation,
  ButtonSize,
  Button,
  Input,
  InputTypes,
  ButtonStyles,
  ButtonTypes,
  Checkbox,
  MLPVersion,
} from '@nextgen-web-framework/all';
import { AmwayPasswordInput, ComponentType, UsernameField } from '@commerce-identity-webcomponents/ui';
import { OperationType } from '@commerce-identity-webcomponents/sdk';
import {
  updateRegistrationData,
  SingupUserDetailsType,
  UsernameContactType,
} from '@commerce-profile-webcomponents/sdk';
import type { RegistrationNextHandlerPatchPayload } from '@commerce-profile-webcomponents/sdk';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { checkUserExists, getJWTToken } from '../../utils/registrationUtils';
import { signupFormValidationSchema } from '../../utils/ValidationSchema';
import { TextConstants } from '../../utils/Constants';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { isEmail } from '../../utils/helper';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import { APICallType, IdentityOnInputChangeCallbackResponse } from '../Registration/types/propTypes';
import type { RegistrationUserCheckType, RegistrationUserDetailsProps } from './types/propTypes';
import styles from './RegistrationUserDetails.module.scss';

const RegistrationUserDetails = ({ nextHandler }: RegistrationUserDetailsProps) => {
  const [disableButton, setDisableButton] = useState(true);
  const [isUniqueProfile, setIsUniqueProfile] = useState(false);
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const { signupFormData, isRedirectedFromModal, currentIndex, userData, OtpData } = registrationFlowData;
  const { patchVersion } = userData;
  const { usernamebkp } = signupFormData;
  const [username, setUsername] = useState({ countryCode: '', amwayId: '', emailOrPhoneWithoutCC: '' });
  const apiErrorHandler = useGlobalErrorHandler();

  const cancelHandler = () => {
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        currentIndex: currentIndex + 1,
        currentStepName: 'contactVerificationStep',
        signupFormData: {
          ...signupFormData,
          userName: usernamebkp,
        },
      }),
    );
  };

  const saveFormData = async (values: SingupUserDetailsType) => {
    const usernameType = isEmail(username.amwayId) ? 'email' : 'phone';
    if (isUniqueProfile) {
      const password = values.password ?? '';
      const res = await getJWTToken(username.amwayId, password, apiErrorHandler);
      if (values.lastName && values.tncCheckbox && values.useCheckbox && values.privacyCheckbox) {
        const slicePayload = {
          signupFormData: {
            ...values,
            userName: username.emailOrPhoneWithoutCC,
            usernameType: usernameType,
            usernamebkp: username.emailOrPhoneWithoutCC,
            modValue: username.amwayId,
            generateOtp: true,
          },
          OtpData: {
            isOTPVerified: false,
          },
          userData: {
            jwtToken: res,
          },
        };

        const payload: RegistrationNextHandlerPatchPayload = {
          userName: username.emailOrPhoneWithoutCC,
          givenName: values.firstName,
          familyName: values.lastName,
          tncCheckbox: values.tncCheckbox,
          useCheckbox: values.useCheckbox,
          privacyCheckbox: values.privacyCheckbox,
          dialCode: username.countryCode,
          identityJWT: res,
          version: patchVersion,
        };
        nextHandler(payload, slicePayload);
      }
    }
  };

  const checkDuplicateUser = (userName: string) => {
    const usernameType = isEmail(username.amwayId) ? 'email' : 'phone';
    const regPayload = {
      usernameContactType: UsernameContactType.Phone,
      phone: userName,
      version: patchVersion,
    };
    const regPatchHandler = () => nextHandler(regPayload, undefined, true, false, APICallType.PATCH);
    checkUserExists(userName, regPatchHandler, setIsUniqueProfile).then((res: RegistrationUserCheckType) => {
      if (res.isUnique) {
        setDisableButton(false);
      } else {
        dispatch(
          updateRegistrationData({
            ...registrationFlowData,
            OtpData: {
              ...OtpData,
              isOTPVerified: false,
            },
            signupFormData: {
              ...signupFormData,
              usernameType: usernameType,
            },
            isDuplicateUser: true,
          }),
        );
      }
    });
  };
  //password props
  const passwordProps = {
    label: 'Password',
  };

  //username component state
  const [error, setError] = useState<string>('');

  const usernameProps = {
    dropdownLabel: t('profile.registration.username.countryText'),
    error,
    setError,
    defaultLabel: t('profile.registration.username.smartLabel'),
    mobileLabel: t('profile.registration.username.mobileText'),
    emailLabel: t('profile.registration.username.emailText'),
    checkUsernameConfig: OperationType.CHECK,
    onInputChangeCallback: ({ countryCode, amwayId, emailOrPhoneWithoutCC }: IdentityOnInputChangeCallbackResponse) => {
      checkDuplicateUser(amwayId as string);
      setUsername({
        countryCode: countryCode as string,
        amwayId: amwayId as string,
        emailOrPhoneWithoutCC: emailOrPhoneWithoutCC as string,
      });
    },
  };

  return (
    <div className={styles['registration-user-details-container']}>
      <div className={styles['registration-user-details-heading']} data-testid="registration-user-details-heading">
        {t('profile.registrationUserDetails.heading')}
      </div>
      <div className={styles['registration-user-details-form']} data-testid="registration-user-details-form">
        <Formik
          initialValues={signupFormData}
          onSubmit={saveFormData}
          validationSchema={signupFormValidationSchema}
          validateOnBlur={true}
          validateOnChange={true}
        >
          {({ values, handleChange, handleBlur, setFieldValue, isValid }) => {
            const isNextButtonEnable =
              values.tncCheckbox &&
              values.useCheckbox &&
              values.privacyCheckbox &&
              !disableButton &&
              isValid &&
              values.password &&
              username.emailOrPhoneWithoutCC.length >= 1 &&
              error.length === 0;
            const setChecboxValue = (check?: string) => {
              setFieldValue(`${check}`, !values[`${check}` as keyof SingupUserDetailsType]);
            };

            return (
              <Form>
                <div className={styles['input-name-fields']}>
                  <div className={styles['input-fname-field']}>
                    <Field type="text" name="firstName">
                      {({ meta }: FieldProps) => (
                        <Input
                          id="firstName"
                          data-testid="firstName"
                          type={InputTypes.TEXT}
                          name="firstName"
                          value={values.firstName}
                          label={t('profile.registrationUserDetails.firstName')}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={50}
                          status={inputErrorStatus(meta.touched, meta.error)}
                          message={inputErrorMessage(t, meta.touched, meta.error, { max: TextConstants.NAME_MAX })}
                        />
                      )}
                    </Field>
                  </div>

                  <div className={styles['input-lname-field']}>
                    <Field type="text" name="lastName">
                      {({ meta }: FieldProps) => (
                        <Input
                          id="lastName"
                          data-testid="lastName"
                          type={InputTypes.TEXT}
                          name="lastName"
                          label={t('profile.registrationUserDetails.lastName')}
                          onChange={handleChange}
                          value={values.lastName}
                          onBlur={handleBlur}
                          status={inputErrorStatus(meta.touched, meta.error)}
                          message={inputErrorMessage(t, meta.touched, meta.error, { max: TextConstants.NAME_MAX })}
                        />
                      )}
                    </Field>
                  </div>
                </div>
                <div className={styles['username-input-field']}>
                  <UsernameField {...usernameProps} />
                </div>
                <div className={styles['password-input-field']}>
                  <AmwayPasswordInput
                    name="createPassword"
                    mlpVersion={MLPVersion.ONE}
                    {...passwordProps}
                    component={ComponentType.WITHOUT_CONFIRM}
                  />
                </div>
                <div className={styles['tnc-checkbox-wrapper']}>
                  <div className={styles['tnc-checkbox']}>
                    <Checkbox
                      data-testid="tnc-checkbox"
                      text={''}
                      checked={values.useCheckbox}
                      onChange={() => setChecboxValue('useCheckbox')}
                    />
                    <div className={styles['tnc-text']}>
                      <span>
                        <span className={styles['tnc-textlink']}>{t('profile.registrationUserDetails.checkbox1')}</span>

                        {/* link will be added in future */}
                        <a className={styles['tnc-hyperlink']} href="www.amway.com" target="_blank">
                          {t('profile.registrationUserDetails.terms-of-use-checkbox')}
                        </a>
                      </span>
                    </div>
                  </div>
                  <div className={styles['tnc-checkbox']}>
                    <Checkbox
                      data-testid="tnc-checkbox-two"
                      text={''}
                      checked={values.privacyCheckbox}
                      onChange={() => setChecboxValue('privacyCheckbox')}
                    />
                    <div className={styles['tnc-text']}>
                      <span>
                        <span className={styles['tnc-textlink']}>{t('profile.registrationUserDetails.checkbox1')}</span>

                        {/* link will be added in future */}
                        <a className={styles['tnc-hyperlink']} href="www.amway.com" target="_blank">
                          {t('profile.registrationUserDetails.privacy-checkbox')}
                        </a>
                      </span>
                    </div>
                  </div>
                  <div className={styles['tnc-checkbox']}>
                    <Checkbox
                      data-testid="tnc-checkbox-three"
                      text={''}
                      checked={values.tncCheckbox}
                      onChange={() => setChecboxValue('tncCheckbox')}
                    />
                    <div className={styles['tnc-text']}>
                      <span>
                        <span className={styles['tnc-textlink']}>{t('profile.registrationUserDetails.checkbox1')}</span>

                        {/* link will be added in future */}
                        <a className={styles['tnc-hyperlink']} href="www.amway.com" target="_blank">
                          {t('profile.registrationUserDetails.checkbox2')}
                        </a>
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles['registration-user-details-btn']}>
                  <Button
                    title={t('profile.common.nextButton')}
                    type={ButtonTypes.SUBMIT}
                    data-testid="next-button-testid"
                    buttonStyle={ButtonStyles.SOLID}
                    size={ButtonSize.MEDIUM}
                    disabled={!isNextButtonEnable}
                  />
                </div>
                <div className={styles['registration-cancel-btn']}>
                  {isRedirectedFromModal && (
                    <Button
                      title={t('profile.registrationUserDetails.cancelButton')}
                      data-testid="cancel-button-testid"
                      buttonStyle={ButtonStyles.OUTLINE}
                      size={ButtonSize.MEDIUM}
                      onClick={cancelHandler}
                    />
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};
export default RegistrationUserDetails;
